import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Calendar, Phone, Mail, ShieldCheck, CheckCircle2, AlertCircle } from "lucide-react";
import { companyInfo } from "../data";
import { fetchPageBySlug } from "../lib/cms";

export default function Booking() {
  const location = useLocation();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Formular-tilstande
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    regNumber: "",
    carModel: "",
    service: "",
    date: "",
    description: "",
    website_url: "" // Honeypot felt
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Forudfyld ydelse hvis sendt med state (f.eks. fra en ydelsesside)
  useEffect(() => {
    if (location.state && location.state.preselectedService) {
      setFormData((prev) => ({
        ...prev,
        service: location.state.preselectedService
      }));
    }
  }, [location]);

  useEffect(() => {
    async function loadData() {
      try {
        const page = await fetchPageBySlug("online-booking");
        setPageData(page);
      } catch (error) {
        console.error("Fejl ved indlæsning af booking data:", error);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  useEffect(() => {
    document.title = "Bestil tid til service af din bil | Service og reparation af biler i Skærbæk | Brdr. Lorenzen ApS";
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Vi udfører hurtigt og pålidelig service af alle bilmærker og modeller - du beholder garantien. Bestil her.');

    let ogImage = document.querySelector('meta[property="og:image"]');
    if (!ogImage) {
      ogImage = document.createElement('meta');
      ogImage.setAttribute('property', 'og:image');
      document.head.appendChild(ogImage);
    }
    ogImage.setAttribute('content', '');
  }, []);

  const servicesList = [
    "Serviceeftersyn",
    "Klargøring til syn",
    "Fejlsøgning",
    "Sommerdæk",
    "Vinterdæk",
    "Dækopbevaring",
    "Pladeværksted",
    "Olieskift/skylning af automatgearkasse",
    "Lånebil"
  ];

  // Validering
  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Navn er påkrævet";
    if (!formData.phone.trim()) {
      newErrors.phone = "Telefonnummer er påkrævet";
    } else if (!/^[0-9\s+]{8,15}$/.test(formData.phone.trim())) {
      newErrors.phone = "Ugyldigt telefonnummer";
    }
    if (!formData.email.trim()) {
      newErrors.email = "Email er påkrævet";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Ugyldig emailadresse";
    }
    if (!formData.description.trim()) newErrors.description = "Beskrivelse er påkrævet";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
    // Fjern fejlbesked, når brugeren skriver
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const response = await fetch("https://api.dev.auxo.dk/webform/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error("Der skete en fejl under afsendelsen. Prøv venligst igen.");
      }

      setSubmitSuccess(true);
      // Nulstil formular
      setFormData({
        name: "",
        phone: "",
        email: "",
        regNumber: "",
        carModel: "",
        service: "",
        date: "",
        description: "",
        website_url: ""
      });
    } catch (error) {
      setSubmitError(error.message || "Kunne ikke oprette forbindelse til serveren.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-slate-50 text-slate-900 antialiased min-h-screen">
      {/* Header Banner */}
      <section className="relative overflow-hidden bg-slate-900 py-24 border-b border-slate-800">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://assets.auxodev.dk/assets/845935e5-3993-406d-896b-e0a4423b6917/cf74c1bb-4b40-4160-820d-1fa8ea095f1d.jpg"
            alt="Værksted"
            className="h-full w-full object-cover filter brightness-30 contrast-105"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/80 to-transparent" />
        </div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-left">
          <h1 className="text-2xl font-extrabold tracking-tight sm:text-4xl md:text-5xl text-white">
            {pageData?.title || "Online Booking"}
          </h1>
          <p className="mt-4 max-w-2xl text-sm md:text-base text-slate-300">
            Bestil tid til service, reparation eller dækskifte nemt og hurtigt online. Vi vender hurtigt tilbage med en bekræftelse.
          </p>
        </div>
      </section>

      {/* Booking Form Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
            {/* Left Column: Intro & Info */}
            <div className="lg:col-span-5 text-left space-y-8">
              <div
                className="prose prose-slate max-w-none text-slate-600 leading-relaxed space-y-6"
                dangerouslySetInnerHTML={{
                  __html: pageData?.content || `
                    <p>Bestil tid til service, reparation eller dækskifte på vores værksted i Skærbæk nemt og hurtigt.</p>
                    <p>Udfyld formularen nedenfor med dine oplysninger, bilens registreringsnummer og den ønskede ydelse, så vender vi hurtigt tilbage med en bekræftelse.</p>
                  `
                }}
              />

              <div className="rounded-xl border border-slate-200 bg-slate-50 p-6 space-y-6 shadow-sm">
                <h3 className="text-sm sm:text-base font-bold text-slate-900 uppercase tracking-wider">
                  Har du spørgsmål?
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Du er altid velkommen til at ringe til os for at booke tid direkte eller få rådgivning om din bil.
                </p>
                <div className="space-y-4 text-sm">
                  <a
                    href={`tel:${companyInfo.phone}`}
                    className="flex items-center gap-3 text-slate-900 hover:text-blue-600 transition-colors font-mono font-semibold"
                  >
                    <Phone className="h-5 w-5 text-blue-500 shrink-0" />
                    <span className="whitespace-nowrap">{companyInfo.phoneFormatted}</span>
                  </a>
                  <a
                    href={`mailto:${companyInfo.email}`}
                    className="flex items-center gap-3 text-slate-900 hover:text-blue-600 transition-colors font-semibold"
                  >
                    <Mail className="h-5 w-5 text-blue-500 shrink-0" />
                    <span className="whitespace-nowrap">{companyInfo.email}</span>
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-3 text-xs text-slate-500">
                <ShieldCheck className="h-5 w-5 text-blue-500 shrink-0" />
                <p className="leading-relaxed">
                  Dine oplysninger behandles fortroligt og udelukkende med det formål at håndtere din bookingforespørgsel.
                </p>
              </div>
            </div>

            {/* Right Column: Form */}
            <div className="lg:col-span-7">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 md:p-10 text-left shadow-lg">
                {submitSuccess ? (
                  <div className="py-8 text-center space-y-6 animate-in fade-in duration-300">
                    <CheckCircle2 className="h-16 w-16 text-emerald-500 mx-auto" />
                    <div className="space-y-2">
                      <h3 className="text-xl sm:text-2xl font-bold text-slate-900">Tak for din forespørgsel!</h3>
                      <p className="text-slate-600 max-w-md mx-auto text-sm leading-relaxed">
                        Vi har modtaget din booking. Vi gennemgår dine ønsker og vender hurtigt tilbage til dig med en bekræftelse på mail eller telefon.
                      </p>
                    </div>
                    <button
                      onClick={() => setSubmitSuccess(false)}
                      className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-6 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 transition-colors"
                    >
                      Send en ny forespørgsel
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {submitError && (
                      <div className="flex items-start gap-3 rounded-lg border border-red-500/20 bg-red-500/5 p-4 text-sm text-red-600">
                        <AlertCircle className="h-5 w-5 shrink-0" />
                        <p>{submitError}</p>
                      </div>
                    )}

                    {/* Antispam Honeypot (Skjult for mennesker) */}
                    <div className="sr-only absolute" aria-hidden="true">
                      <label htmlFor="website_url">Hjemmeside URL</label>
                      <input
                        type="text"
                        id="website_url"
                        name="website_url"
                        value={formData.website_url}
                        onChange={handleChange}
                        tabIndex="-1"
                        autoComplete="off"
                      />
                    </div>

                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                      {/* Navn */}
                      <div className="space-y-2">
                        <label htmlFor="name" className="text-xs font-bold uppercase tracking-wider text-slate-600">
                          Navn <span className="text-blue-600">*</span>
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          className={`w-full rounded-lg border bg-white px-4 py-3 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all ${
                            errors.name ? "border-red-500/50 focus:border-red-500" : "border-slate-200 focus:border-blue-500"
                          }`}
                          placeholder="Dit fulde navn"
                        />
                        {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
                      </div>

                      {/* Telefonnummer */}
                      <div className="space-y-2">
                        <label htmlFor="phone" className="text-xs font-bold uppercase tracking-wider text-slate-600">
                          Telefonnummer <span className="text-blue-600">*</span>
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          required
                          value={formData.phone}
                          onChange={handleChange}
                          className={`w-full rounded-lg border bg-white px-4 py-3 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all ${
                            errors.phone ? "border-red-500/50 focus:border-red-500" : "border-slate-200 focus:border-blue-500"
                          }`}
                          placeholder="F.eks. 74751417"
                        />
                        {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone}</p>}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                      {/* Email */}
                      <div className="space-y-2">
                        <label htmlFor="email" className="text-xs font-bold uppercase tracking-wider text-slate-600">
                          Email <span className="text-blue-600">*</span>
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          className={`w-full rounded-lg border bg-white px-4 py-3 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all ${
                            errors.email ? "border-red-500/50 focus:border-red-500" : "border-slate-200 focus:border-blue-500"
                          }`}
                          placeholder="din@email.dk"
                        />
                        {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
                      </div>

                      {/* Registreringsnummer */}
                      <div className="space-y-2">
                        <label htmlFor="regNumber" className="text-xs font-bold uppercase tracking-wider text-slate-600">
                          Registreringsnummer
                        </label>
                        <input
                          type="text"
                          id="regNumber"
                          name="regNumber"
                          value={formData.regNumber}
                          onChange={handleChange}
                          className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all uppercase"
                          placeholder="F.eks. AB12345"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                      {/* Bilmodel */}
                      <div className="space-y-2">
                        <label htmlFor="carModel" className="text-xs font-bold uppercase tracking-wider text-slate-600">
                          Bilmodel (Mærke & Model)
                        </label>
                        <input
                          type="text"
                          id="carModel"
                          name="carModel"
                          value={formData.carModel}
                          onChange={handleChange}
                          className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all"
                          placeholder="F.eks. Ford Fiesta 1.0"
                        />
                      </div>

                      {/* Ønsket ydelse */}
                      <div className="space-y-2">
                        <label htmlFor="service" className="text-xs font-bold uppercase tracking-wider text-slate-600">
                          Ønsket ydelse
                        </label>
                        <select
                          id="service"
                          name="service"
                          value={formData.service}
                          onChange={handleChange}
                          className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all"
                        >
                          <option value="">Vælg ydelse...</option>
                          {servicesList.map((service) => (
                            <option key={service} value={service}>
                              {service}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Ønsket dato */}
                    <div className="space-y-2">
                      <label htmlFor="date" className="text-xs font-bold uppercase tracking-wider text-slate-600">
                        Ønsket dato
                      </label>
                      <input
                        type="date"
                        id="date"
                        name="date"
                        value={formData.date}
                        onChange={handleChange}
                        className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all"
                      />
                    </div>

                    {/* Beskrivelse */}
                    <div className="space-y-2">
                      <label htmlFor="description" className="text-xs font-bold uppercase tracking-wider text-slate-600">
                        Beskrivelse / Besked <span className="text-blue-600">*</span>
                      </label>
                      <textarea
                        id="description"
                        name="description"
                        required
                        rows="4"
                        value={formData.description}
                        onChange={handleChange}
                        className={`w-full rounded-lg border bg-white px-4 py-3 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all ${
                          errors.description ? "border-red-500/50 focus:border-red-500" : "border-slate-200 focus:border-blue-500"
                        }`}
                        placeholder="Beskriv opgaven eller fejlen på din bil..."
                      />
                      {errors.description && <p className="text-xs text-red-500 mt-1">{errors.description}</p>}
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 py-4 text-base font-bold text-white hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-600/10 hover:shadow-blue-600/20"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Sender forespørgsel...
                        </>
                      ) : (
                        <>
                          <Calendar className="h-5 w-5" />
                          Send Bookingforespørgsel
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
